import { Box, Button } from '@atgfr8/atg-ui-tailwind';
import { MessageIcon } from '@atgfr8/icons';
import { BasicTable, type Column } from '@components/BasicTable/BasicTable';
import { BasicModal } from '@components/Modal/BasicModal';
import { SelectInput } from '@components/Select/SelectInput';
import { CardPlaceholder } from '@components/Skeleton/Skeleton';
import type { SidebarMenuLabelTypes } from '@components/Sidebar/Sidebar';
import { ToastType } from '@components/Toast/Toast';
import { useNavigate } from '@solidjs/router';
import { sendDirectMessage } from '@store/Global/service';
import { sidebarDataStore } from '@store/Global/store';
import { closeModal, openModal } from '@store/modals/store';
import {
  canDecideRateRequest,
  decideRateRequest,
  filterMaxRateRequests,
  formatRateRequestDate,
  groupMaxRateRequests,
  isRateRequestSubmitting,
} from '@store/orders/actions/rateRequestActions';
import type {
  MaxRateRequestFilter,
  MaxRateRequestGroup,
} from '@typeDefinitions/rateRequestTypes';
import { buildAnnouncementPayload } from '@utils/appMessage';
import { formatAmount, handleToast } from '@utils/utils';
import { DenyRateRequestModal } from '@views/order/components/carrier/maxRateRequest/MaxRateRequestBanner';
import {
  createMemo,
  createSignal,
  For,
  lazy,
  type Setter,
  Show,
} from 'solid-js';

const RichTextEditor = lazy(
  () => import('@components/RichTextEditor/RichTextEditor'),
);

const DENY_MODAL_ID = 'deny-rate-request-carrier-approvals';
const MESSAGE_MODAL_ID = 'max-rate-request-direct-message';

const FILTER_OPTIONS: { label: string; value: MaxRateRequestFilter }[] = [
  { label: 'Pending', value: 'Pending' },
  { label: 'Resolved', value: 'Resolved' },
  { label: 'All', value: 'All' },
];

const linkClass = 'text-[#006C9D] underline cursor-pointer text-left';
const buttonClass = 'rounded-[4px] whitespace-nowrap';

type Recipient = { id: number; name: string };

const DirectMessageModal = (props: {
  recipient: Recipient | null;
  onClose: () => void;
}) => {
  const [message, setMessage] = createSignal('');
  const [sending, setSending] = createSignal(false);

  const close = () => {
    setMessage('');
    closeModal(MESSAGE_MODAL_ID);
    props.onClose();
  };

  const send = async () => {
    const recipient = props.recipient;
    const content = message().trim();
    if (recipient === null || content === '' || sending()) {
      return;
    }
    setSending(true);
    try {
      await sendDirectMessage(recipient.id, buildAnnouncementPayload(content));
      handleToast(ToastType.Success, 'Message sent');
      close();
    } catch (error: unknown) {
      handleToast(ToastType.Error, (error as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <BasicModal
      id={MESSAGE_MODAL_ID}
      title={`Message ${props.recipient?.name ?? ''}`}
      width="640px"
      onSubmitText="Send"
      onSubmit={() => void send()}
      onSubmitLoading={sending()}
      preventCloseOnSubmit
      closeDisabled={sending()}
      onClose={close}
    >
      <Show when={props.recipient}>
        <RichTextEditor
          value={message()}
          onChange={setMessage}
          maxLength={10_000}
          ariaLabel="Direct message"
          placeholder="Write a message"
        />
      </Show>
    </BasicModal>
  );
};

const LineItemCells = (props: {
  request: MaxRateRequestGroup;
  field: 'type' | 'amount';
}) => (
  <Show
    when={props.request.lineItems.length > 0}
    fallback={<span>{props.field === 'type' ? '-' : formatAmount(0)}</span>}
  >
    <div class="flex flex-col gap-1">
      <For each={props.request.lineItems}>
        {(item) => (
          <span class="whitespace-nowrap">
            {props.field === 'type' ? item.type : formatAmount(item.amount)}
          </span>
        )}
      </For>
    </div>
  </Show>
);

const MaxRateRequests = (props: {
  setChecked: Setter<SidebarMenuLabelTypes>;
}) => {
  const navigate = useNavigate();
  const [filter, setFilter] = createSignal<MaxRateRequestFilter>('Pending');
  const [denyRequest, setDenyRequest] =
    createSignal<MaxRateRequestGroup | null>(null);
  const [recipient, setRecipient] = createSignal<Recipient | null>(null);

  const requests = createMemo(() =>
    filterMaxRateRequests(
      groupMaxRateRequests(sidebarDataStore.maxRateRequestsData.data),
      filter(),
    ),
  );

  const openLoad = (request: MaxRateRequestGroup) => {
    navigate(`/order/Details/${request.orderId}`);
    props.setChecked('');
  };

  const openMessage = (request: MaxRateRequestGroup) => {
    setRecipient({
      id: request.requestedById,
      name: request.requestedByName ?? 'user',
    });
    openModal(MESSAGE_MODAL_ID);
  };

  const openDeny = (request: MaxRateRequestGroup) => {
    setDenyRequest(request);
    openModal(DENY_MODAL_ID);
  };

  const ActionCell = (cellProps: { request: MaxRateRequestGroup }) => (
    <Show
      when={
        cellProps.request.requestStatus === 'Pending' &&
        canDecideRateRequest({ requestedBy: cellProps.request.requestedById })
      }
      fallback={
        <span
          class={`font-medium ${
            cellProps.request.requestStatus === 'Approved'
              ? 'text-[#248670]'
              : cellProps.request.requestStatus === 'Denied'
                ? 'text-[#B00020]'
                : ''
          }`}
          data-testid="max-rate-request-status"
        >
          {cellProps.request.requestStatus}
        </span>
      }
    >
      <div class="flex items-center gap-2">
        <Button
          class={buttonClass}
          variant="contained"
          size="sm"
          disabled={isRateRequestSubmitting(cellProps.request.loadId)}
          onClick={() => void decideRateRequest(cellProps.request, true)}
          dataTestId="carrier-approvals-approve-max-rate-button"
        >
          Approve
        </Button>
        <Button
          class={buttonClass}
          variant="outlined"
          color="error"
          size="sm"
          disabled={isRateRequestSubmitting(cellProps.request.loadId)}
          onClick={() => openDeny(cellProps.request)}
          dataTestId="carrier-approvals-deny-max-rate-button"
        >
          Deny
        </Button>
      </div>
    </Show>
  );

  const columns = (): Column<MaxRateRequestGroup>[] => [
    {
      name: 'Load Number',
      key: 'loadNumber',
      renderCell: (row) => (
        <button
          type="button"
          class={`${linkClass} whitespace-nowrap`}
          onClick={() => openLoad(row)}
          data-testid="max-rate-request-load-link"
        >
          {row.loadNumber}
        </button>
      ),
    },
    {
      name: 'Requested Date/Time',
      key: 'requestedDate',
      renderCell: (row) => (
        <span class="whitespace-nowrap">
          {formatRateRequestDate(row.requestedDate)}
        </span>
      ),
    },
    {
      name: 'Requested By',
      key: 'requestedByName',
      renderCell: (row) => (
        <div class="flex items-center gap-2">
          <button
            type="button"
            class={linkClass}
            onClick={() => openMessage(row)}
            data-testid="max-rate-request-requested-by"
          >
            {row.requestedByName ?? '-'}
          </button>
          <button
            type="button"
            aria-label="Send direct message"
            class="text-[#006C9D] cursor-pointer"
            onClick={() => openMessage(row)}
          >
            <MessageIcon class="size-4!" />
          </button>
        </div>
      ),
    },
    {
      name: 'Current Max Rate',
      key: 'currentMaxRate',
      renderCell: (row) => formatAmount(row.currentMaxRate),
    },
    {
      name: 'Line Item Type',
      key: 'lineItemType',
      renderCell: (row) => <LineItemCells request={row} field="type" />,
    },
    {
      name: 'Line Item Amount',
      key: 'lineItemAmount',
      renderCell: (row) => <LineItemCells request={row} field="amount" />,
    },
    {
      name: 'Requested Max Rate',
      key: 'requestedMaxRate',
      renderCell: (row) => formatAmount(row.requestedMaxRate),
    },
    ...(filter() === 'Pending'
      ? []
      : [
          {
            name: 'Action Taken By',
            key: 'actionTakenByName',
            renderCell: (row: MaxRateRequestGroup) =>
              row.actionTakenByName ?? '-',
          },
          {
            name: 'Action Taken On',
            key: 'actionTakenOnDate',
            renderCell: (row: MaxRateRequestGroup) => (
              <span class="whitespace-nowrap">
                {row.actionTakenOnDate
                  ? formatRateRequestDate(row.actionTakenOnDate)
                  : '-'}
              </span>
            ),
          },
          {
            name: 'Notes',
            key: 'notes',
            renderCell: (row: MaxRateRequestGroup) => (
              <span class="block min-w-[160px] break-words">
                {row.notes ?? '-'}
              </span>
            ),
          },
        ]),
    {
      name: 'Action',
      key: 'requestStatus',
      renderCell: (row) => <ActionCell request={row} />,
    },
  ];

  return (
    <Box class="w-[700px] max-w-full bg-white p-2">
      <div class="w-[180px] mb-2">
        <SelectInput
          label="Show"
          size="sm"
          menuItems={FILTER_OPTIONS}
          value={filter()}
          onChange={(value) => setFilter(value as MaxRateRequestFilter)}
          dataTestId="max-rate-requests-show-select"
        />
      </div>
      <Show
        when={!sidebarDataStore.maxRateRequestsData.loading}
        fallback={<CardPlaceholder />}
      >
        <Box class="min-h-[calc(100vh-340px)]! max-h-[calc(100vh-340px)]! h-auto overflow-auto">
          <BasicTable<MaxRateRequestGroup>
            columns={columns()}
            rows={requests()}
            columnNoWrap
            tableContainerClasses="text-black! [&_td]:!border-[#c4c4c4] [&_th]:!border-[#c4c4c4] [&>div]:overflow-visible! [&_th]:sticky [&_th]:top-0 [&_th]:z-[1] [&_th]:bg-[#f2f2f2]!"
            tableHeaderClasses="bg-[#f2f2f2]"
          />
        </Box>
      </Show>
      <DenyRateRequestModal modalId={DENY_MODAL_ID} request={denyRequest()} />
      <DirectMessageModal
        recipient={recipient()}
        onClose={() => setRecipient(null)}
      />
    </Box>
  );
};

export default MaxRateRequests;
